import Axios, { type AxiosRequestConfig } from 'axios';
import { faSuitcaseRolling } from '@fortawesome/pro-regular-svg-icons';
import TripPriceClass from '../TripPriceClass';
import FlightBillHandler from './FlightBillHandler';
import VerifyResult from '@itinerary/VerifyResult';
import PassengerForm from '@itinerary/PassengerForm';
import {
    ItineraryVerifyException,
    VerificationStatus,
} from '@itinerary/Exceptions';
import {
    AuxiliaryServiceApplicabilityTypes,
    BaggageType,
    CabinClassRequest,
    FlightsApiApiFlightsGetBaggageInfoPost,
    FlightsApiApiFlightsReservePost,
    type PriceDto,
    type ReserveFlightRequest,
} from '@clients/FlightApi';
import { FlightServiceException } from '@itinerary/trip/flight/FlightModuleTypes';
import {
    type PassengerCount,
    type PassengerFare,
    type OptionalService,
    type OptionalServiceItem,
} from '@itinerary/types';
import { BuyExtraFields, PassengerFields } from '@store/form';
import { DiscountType, type InvoiceEntry } from '@store/invoice';
import { PaxType } from '@utils/types';
import { PaxTypeTitles } from '@itinerary/trip/TripModuleTypes';
import { CabinClasses } from '@itinerary/trip/flight/types';
import { type SegmentOption } from '@itinerary/trip/types';

export default class FlightPriceClass extends TripPriceClass {
    private readonly _preDiscountPassengerFares: PassengerFare[] = [];

    baggage = 0;

    isCharter = false;

    hasBaggage = false;
    needsLoadBaggage = false;
    baggageLoadingStatus: 'None' | 'Loading' | 'Done' = 'None';

    override get priceOptions(): SegmentOption[] {
        if (!this.international || !this.baggage) {
            return [];
        }
        return [
            {
                title: `بار رایگان: ${this.baggageTypeText}`,
                icon: faSuitcaseRolling,
                loading: this.baggageLoadingStatus === 'Loading',
            },
        ];
    }

    priceClass = '';

    cabinClass: CabinClassRequest = CabinClassRequest.Unknown;

    override capacity = 0;
    override get prePriceDiscountPercent() {
        const serviceType = toValue(this.servicesPreDiscountPercent);
        return this.international
            ? serviceType.InternationalFlight
            : serviceType.Flight;
    }

    uniqueId = '';

    override showCapacity = true;

    override capacityText = '';

    override loadableTermsUrl: string | null = null;

    override cancellationTerms: string | null = '';

    discountToken = '';

    proposalId = '';

    override title = '';

    override descriptionItems: string[] = [];

    baggageType: BaggageType = BaggageType.None;
    providerBaggageType?: string;

    get baggageTypeText() {
        return (
            this.providerBaggageType ||
            `${this.baggage} ${this.baggageType === 'KG' ? 'کیلوگرم' : 'بسته'}`
        );
    }

    constructor(
        price?: PriceDto,
        public international?: boolean,
        private readonly paxCount?: PassengerCount
    ) {
        super();

        if (price == null) {
            return;
        }
        this.hasPriority = !!price.IsAgencyHasPriorityOnShowingFlight;
        this.uniqueId = price.UniqueId ?? '';
        this.isCharter = !!price.IsCharter;
        this.baggage = price.Baggage ?? 0;
        this.cabinClass = price.CabinClass ?? this.cabinClass;
        this.baggageType = price.BaggageType ?? BaggageType.None;
        this.providerBaggageType = price.ProviderBaggageType || undefined;
        this.hasBaggage = price.Baggage === null || (price.Baggage ?? 0) > 0;
        this.priceClass = price.BookingClass ?? '';
        this.cabinClass = price.CabinClass ?? CabinClassRequest.Unknown;

        this._preDiscountPassengerFares =
            price.PassengerFares?.map(fare => ({
                price: fare.TotalFare ?? 0,
                discount: fare.Discount ?? 0,
                paxType:
                    fare.PaxType === 'ADL'
                        ? PaxType.Adult
                        : fare.PaxType === 'CHD'
                          ? PaxType.Child
                          : PaxType.Infant,
            })) || [];

        this.capacity = price.Capacity ?? 0;
        this.capacityText =
            this.capacity >= 9 ? '+9' : this.capacity.toString();
        this.cancellationTerms =
            price.FareRules?.replace(/ (\d)\)/g, '\n$1)') || null;
        this.discountToken = price.DiscountToken ?? '';
        this.proposalId = price.ProposalId ?? '';
        this.title = `${
            CabinClasses.find(({ Id }) => Id === this.cabinClass)?.Title
        } (${this.cabinClass === 'All' ? '' : 'کلاس نرخی'} ${this.priceClass})`;
        this.needsLoadBaggage = !!price.NeedGetPrice;

        this.descriptionItems = [
            this.baggage === 0
                ? 'این پرواز بار رایگان ندارد'
                : this.baggage
                  ? `میزان بار مجاز برای این پرواز ${this.baggageTypeText} می‌باشد.`
                  : '',
        ].filter(p => p);
        this.loadableTermsUrl = price.FareRulesUrl ?? '';
        if (!this.isCharter && !this.international) {
            this.priceDescription = 'نرخ رسمی ایرلاین';
        }
    }

    async loadBaggage() {
        if (this.baggageLoadingStatus === 'Done' || !this.needsLoadBaggage) {
            return;
        }
        this.baggageLoadingStatus = 'Loading';
        try {
            const { data } = await FlightsApiApiFlightsGetBaggageInfoPost({
                proposalId: this.proposalId,
            });
            if (data) {
                this.hasBaggage = !!data.HasBaggage;
                this.baggage = data.Baggage ?? 0;
                this.priceClass = data.BookingClass ?? '';
                this.baggageType = data.BaggageType ?? BaggageType.None;
            }
        } catch (e) {
            reportException(e);
        } finally {
            this.baggageLoadingStatus = 'Done';
        }
    }

    override get totalPriceForCheck(): number | null {
        if (this.paxCount) {
            return (
                ((this.adultFare &&
                    this.adultFare.price + this.adultFare.discount) ||
                    0) *
                    this.paxCount.adultCount +
                ((this.childFare &&
                    this.childFare.price + this.childFare.discount) ||
                    0) *
                    this.paxCount.childCount +
                ((this.infantFare &&
                    this.infantFare.price + this.infantFare.discount) ||
                    0) *
                    this.paxCount.infantCount
            );
        }
        return null;
    }

    override get totalPayableForCheck(): number | null {
        if (this.paxCount) {
            return (
                (this.adultFare?.price || 0) * this.paxCount.adultCount +
                (this.childFare?.price || 0) * this.paxCount.childCount +
                (this.infantFare?.price || 0) * this.paxCount.infantCount
            );
        }
        return null;
    }

    override get singlePrice(): number {
        return this.singlePayable + (this.adultFare?.discount ?? 0);
    }

    override get netPrice(): number {
        return this.adultFare ? this.adultFare.price : 0;
    }

    override get prePricePayable() {
        return this.netPrice;
    }

    override get passengerFares(): PassengerFare[] {
        return this.getPassengerFaresBaseOnCount(this.paxCount);
    }

    private getPassengerFaresBaseOnCount(count?: {
        adultCount: number;
        childCount: number;
        infantCount: number;
    }): PassengerFare[] {
        if (this.couponCode && count) {
            const totalPax =
                count.adultCount + count.childCount + count.infantCount;
            const totalFare = this._passengerFares.reduce((t, f) => {
                switch (f.paxType) {
                    case PaxType.Adult:
                        return t + count.adultCount * f.price;
                    case PaxType.Child:
                        return t + count.childCount * f.price;
                    case PaxType.Infant:
                        return t + count.infantCount * f.price;
                    default:
                        return t;
                }
            }, 0);

            const totalDiscount =
                (totalFare / 100) * this.couponCode.discountPercent;
            const maxDiscount = totalPax * this.couponCode!.maxDiscount;
            const discount =
                totalDiscount > maxDiscount ? maxDiscount : totalDiscount;
            return this._passengerFares.map(f => {
                const d = (discount * f.price) / totalFare;
                return {
                    ...f,
                    price: f.price - d,
                    discount: f.discount + d,
                };
            });

            // true way ToDo: fix it after back change
            // return this._passengerFares.map(f => {
            //  let discount =
            //        (f.price / 100) * this.couponCode!.discountPercent;
            //  discount =
            //      discount > this.couponCode!.maxDiscount
            //          ? this.couponCode!.maxDiscount
            //          : discount;
            //  return {
            //      ...f,
            //      price: f.price - discount,
            //      discount: f.discount + discount
            //  };
            // });
        } else {
            return this._passengerFares;
        }
    }

    private get _passengerFares(): PassengerFare[] {
        return this._preDiscountPassengerFares?.map(fare => ({
            price: this.getPriceWithPreDiscount(fare.price ?? 0),
            discount: this.getPriceWithPreDiscount(fare.discount ?? 0),
            paxType: fare.paxType,
        }));
    }

    get totalCouponDiscount(): number {
        return this.passengerFares.reduce((t, f) => {
            switch (f.paxType) {
                case PaxType.Adult:
                    return t + this.paxCount!.adultCount * f.discount;
                case PaxType.Child:
                    return t + this.paxCount!.childCount * f.discount;
                case PaxType.Infant:
                    return t + this.paxCount!.infantCount * f.discount;
                default:
                    return t;
            }
        }, 0);
    }

    private async reserve(
        request: ReserveFlightRequest,
        params: AxiosRequestConfig = {}
    ) {
        try {
            const { data } = await FlightsApiApiFlightsReservePost(
                {
                    reserveFlightRequest: request,
                },
                params
            );
            return data;
        } catch (e) {
            if (Axios.isAxiosError(e)) {
                throw new FlightServiceException(
                    e.response?.data?.Message,
                    e.response?.data?.ShowAndReturn
                );
            } else {
                throw new FlightServiceException('');
            }
        }
    }

    override async verify(
        count: {
            adultCount: number;
            childCount: number;
            infantCount: number;
        },
        billId: number | null
    ): Promise<VerifyResult> {
        const defaultFields =
            PassengerFields.FirstName |
            PassengerFields.LastName |
            PassengerFields.Gender |
            PassengerFields.Nationality |
            PassengerFields.BirthDate;

        let iranianExtraFields = PassengerFields.NationalCode;
        const foreignerExtraFields =
            PassengerFields.NationalityCode |
            PassengerFields.PassportExpiry |
            PassengerFields.PassportNumber;

        let buyExtraFields = BuyExtraFields.None;
        if (this.international) {
            iranianExtraFields |=
                PassengerFields.PassportExpiry | PassengerFields.PassportNumber;
        }

        try {
            const response = await this.reserve({
                AdultCount: count.adultCount,
                ChildCount: count.childCount,
                InfantCount: count.infantCount,
                CabinClass: this.cabinClass,
                DiscountToken: this.discountToken,
                ProposalId: this.proposalId,
                BillId: billId || undefined,
                Baggage: !!this.baggage,
            });
            const infantOptionalServices: OptionalService[] = [];
            const childOptionalServices: OptionalService[] = [];
            const adultOptionalServices: OptionalService[] = [];
            const optionalServiceTokens: string[] = [];
            if (
                response.Itinerary?.Segments?.some(s =>
                    s.Legs?.some(l => l.AirlineAuxiliaryServices?.length)
                )
            ) {
                buyExtraFields |= BuyExtraFields.PackageOptionalService;
                const defaultOption: OptionalServiceItem = {
                    id: 'none',
                    label: 'بدون خدمات اضافه',
                    price: 0,
                };
                response.Itinerary?.Segments?.forEach(seg => {
                    seg.Legs?.forEach(leg => {
                        const title = `انتخاب خدمات ${leg.Origin} به ${leg.Destination}`;
                        const adultServices: OptionalServiceItem[] = [];
                        const childServices: OptionalServiceItem[] = [];
                        const infantServices: OptionalServiceItem[] = [];
                        leg.AirlineAuxiliaryServices?.forEach(aux => {
                            if (
                                aux.ApplicabilityType ===
                                AuxiliaryServiceApplicabilityTypes.PerPassenger
                            ) {
                                optionalServiceTokens.push(aux.Token ?? '');
                                adultServices.push({
                                    id: aux.Token ?? '',
                                    label: aux.PersianName ?? '',
                                    price: aux.AdultAmount ?? 0,
                                });
                                childServices.push({
                                    id: aux.Token ?? '',
                                    label: aux.PersianName ?? '',
                                    price: aux.ChildAmount ?? 0,
                                });
                                infantServices.push({
                                    id: aux.Token ?? '',
                                    label: aux.PersianName ?? '',
                                    price: aux.InfantAmount ?? 0,
                                });
                            }
                        });
                        if (adultServices.length) {
                            adultOptionalServices.push({
                                title,
                                selectedService: defaultOption,
                                services: [defaultOption, ...adultServices],
                            });
                        }
                        if (childServices.length) {
                            childOptionalServices.push({
                                title,
                                selectedService: defaultOption,
                                services: [defaultOption, ...childServices],
                            });
                        }
                        if (infantServices.length) {
                            infantOptionalServices.push({
                                title,
                                selectedService: defaultOption,
                                services: [defaultOption, ...infantServices],
                            });
                        }
                    });
                });
            }
            const countMap: Record<string, number> = {
                ADL: count.adultCount,
                CHD: count.childCount,
                INF: count.infantCount,
            };

            let totalFare =
                response.Itinerary?.Prices?.reduce(
                    (acc, p) =>
                        (p.PassengerFares?.reduce(
                            (acc, f) =>
                                this.getPriceWithPreDiscount(f.TotalFare ?? 0) *
                                    countMap[f.PaxType || ''] +
                                acc,
                            0
                        ) || 0) + acc,
                    0
                ) || 0;
            let discount =
                response.Itinerary?.Prices?.reduce(
                    (acc, p) =>
                        (p.PassengerFares?.reduce(
                            (acc, f) =>
                                (f.Discount || 0) * countMap[f.PaxType || ''] +
                                acc,
                            0
                        ) || 0) + acc,
                    0
                ) || 0;

            let discountType = DiscountType.Special;

            if (this.couponCode) {
                totalFare -= this.totalCouponDiscount;
                discount += this.totalCouponDiscount;
                discountType = DiscountType.FirstPurchase;
            }

            const adultForm: PassengerForm = new PassengerForm({
                title: PaxTypeTitles[PaxType.Adult],
                type: PaxType.Adult,
                defaultFields,
                iranianExtraFields,
                foreignerExtraFields,
                buyExtraFields,
                optionalServiceList: adultOptionalServices,
            });

            const childForm: PassengerForm = new PassengerForm({
                title: PaxTypeTitles[PaxType.Child],
                type: PaxType.Child,
                defaultFields,
                iranianExtraFields,
                foreignerExtraFields,
                buyExtraFields,
                optionalServiceList: childOptionalServices,
            });

            const infantForm: PassengerForm = new PassengerForm({
                title: PaxTypeTitles[PaxType.Infant],
                type: PaxType.Infant,
                defaultFields,
                iranianExtraFields,
                foreignerExtraFields,
                buyExtraFields,
                optionalServiceList: infantOptionalServices,
            });

            return new VerifyResult(
                new FlightBillHandler(
                    response.Token ?? '',
                    optionalServiceTokens
                ),
                {
                    billCode: response.BillCode || undefined,
                    remainingTime: 600,
                    payablePrice: totalFare,
                    totalPrice: totalFare + discount,
                    haveCustomDiscount: !!this.prePriceDiscountPercent,
                    priceEntries: [
                        {
                            prices:
                                response.Itinerary?.Prices?.[0].PassengerFares?.map(
                                    f => ({
                                        count: countMap[f.PaxType || ''],
                                        paxType:
                                            f.PaxType === 'ADL'
                                                ? PaxType.Adult
                                                : f.PaxType === 'CHD'
                                                  ? PaxType.Child
                                                  : PaxType.Infant,
                                        discount: f.Discount,
                                        price:
                                            (this.getPriceWithPreDiscount(
                                                f.TotalFare ?? 0
                                            ) || 0) + (f.Discount || 0),
                                    })
                                ) || [],
                        },
                    ],
                    discount,
                    discountType,
                    adultForm,
                    childForm,
                    infantForm,
                    paxList: [
                        ...Array(count.adultCount)
                            .fill(0)
                            .map(_ => adultForm.clone()),

                        ...Array(count.childCount)
                            .fill(0)
                            .map(_ => childForm.clone()),

                        ...Array(count.infantCount)
                            .fill(0)
                            .map(_ => infantForm.clone()),
                    ],
                    cancellationTerms:
                        response.Itinerary?.Prices?.[0].FareRules?.replace(
                            / (\d)\)/g,
                            '\n$1)'
                        ) || '',
                    billId: response.BillId,
                    needsDestinationMobile: this.international,
                    warningMessages:
                        response.ReserveNotes?.map(n => ({
                            message: n.Message || '',
                            type: 'unknown',
                        })) || [],
                    loadDefaultPassenger: false,
                    departureTime: dayConfig
                        .gregory(
                            response.Itinerary?.Segments?.[0].Legs?.[0].DepartureTime?.substring(
                                0,
                                19
                            )
                        )
                        .toDate(),
                    maxCapacity: response.Itinerary?.Prices?.[0].Capacity,
                    extraInfoFields: response.ExtraInfoField ?? [],
                }
            );
        } catch (ex) {
            if (ex instanceof FlightServiceException && ex.showAndReturn) {
                throw new ItineraryVerifyException(
                    VerificationStatus.ShowAndReturn,
                    ex.message
                );
            } else if (ex instanceof FlightServiceException) {
                throw new ItineraryVerifyException(
                    VerificationStatus.UnknownError,
                    ex.message
                );
            }
            throw new ItineraryVerifyException(
                VerificationStatus.UnknownError,
                (ex as any).response
            );
        }
    }

    /**
     * Loads the fares of different types of passengers of the flight
     */
    override loadPreInvoice(count: {
        adultCount: number;
        childCount: number;
        infantCount: number;
    }): Promise<InvoiceEntry[]> {
        const entries: InvoiceEntry[] = this.getPassengerFaresBaseOnCount(count)
            .map(f => ({
                ...f,
                price: f.price,
                discount: f.discount,
                discountType: this.couponCode
                    ? DiscountType.FirstPurchase
                    : undefined,
                count:
                    f.paxType === PaxType.Adult
                        ? count.adultCount
                        : f.paxType === PaxType.Child
                          ? count.childCount
                          : count.infantCount,
            }))
            .filter(i => i.count > 0);
        return Promise.resolve(entries);
    }
}
